export type AutocompleteMode = '' | 'relatedUser' | 'relatedNotifier';

export interface IData {
    Person: IPersonListItem[] | null,
    ItSystem: IItSystemListItem[] | null,
    Agreement: IAgreementListItem[] | null,
    License: ILicenseListItem[] | null,
    Service: IServiceListItem[] | null,

    Computer: IDeviceListItem[] | null,
    DataCarrier: IDeviceListItem[] | null,
    MobileDevice: IDeviceListItem[] | null,
    Monitor: IDeviceListItem[] | null,
    NetworkDevice: IDeviceListItem[] | null,
    Printer: IDeviceListItem[] | null
    Accessory: IDeviceListItem[] | null,
    PhoneNumber: IDeviceListItem[] | null,
}

export type KindsList = keyof IData;

export interface ILogPlusResourcesData {
    Person?: IPersonListItem[],
    ItSystem?: IItSystemListItem[],
    Agreement?: IAgreementListItem[],
    License?: ILicenseListItem[],
    Service?: IServiceListItem[],

    Computer?: IDeviceListItem[],
    DataCarrier?: IDeviceListItem[],
    MobileDevice?: IDeviceListItem[],
    Monitor?: IDeviceListItem[],
    NetworkDevice?: IDeviceListItem[],
    Printer?: IDeviceListItem[],
    Accessory?: IDeviceListItem[],
    PhoneNumber?: IDeviceListItem[],
}

export type IAnyResourceListItem = IDeviceListItem | IPersonListItem | IItSystemListItem | IAgreementListItem | ILicenseListItem | IServiceListItem

export interface IPersonListItem {
    Id: number;
    UserName: string;
    Status: string;
    Login: string;
    Email: string;
    Cellphone: string;
    WorkPhone: string;
    FirstName: string;
    LastName: string;
    JobPosition: string;
    Company: string;
    SapId: string;
    MpkNumber: string;
    SupervisorId: number | null;
    SupervisorName: string;
}

export interface IItSystemListItem {
    Id: number;
    Status: string;
    Name: string;
    EnvironmentType: string,
    Type: string;
    Description: string;
}

export interface ILicenseListItem {
    Id: number;
    Status: string;
    Name: string;
    LicenseType: string;
    LicenseTypeDescription: string,
    Description: string;
}

export interface IRelatedLicenseListItem {
    Id: number;
    Status: string;
    Product: string;
    LicenseType: string;
    LicenseTypeDescription: string,
    Description: string;
}

export function parseToLicense(source: IRelatedLicenseListItem): ILicenseListItem {
    return {
        Id: source.Id,
        Status: source.Status,
        Name: source.Product,
        Description: source.Description,
        LicenseType: source.LicenseType,
        LicenseTypeDescription: source.LicenseTypeDescription,
    }
}

export function parseFromLicense(source: ILicenseListItem): IRelatedLicenseListItem {
    return {
        Id: source.Id,
        Status: source.Status,
        Product: source.Name,
        Description: source.Description,
        LicenseType: source.LicenseType,
        LicenseTypeDescription: source.LicenseTypeDescription,
    }
}

export interface IAgreementListItem {
    Id: number;
    Status: string;
    Name: string;
    Type: string;
    Description: string;
    AgreementNumber: string;
    ValidFrom: string;
    ValidTo: string;
}

export interface IRelatedAgreementListItem {
    Id: number;
    Status: string;
    Name: string;
    Type: string;
    AgreementNumber: string,
    Description: string;
    ValidFrom: string;
    ValidTo: string;
}

export function parseToAgreement(source: IRelatedAgreementListItem): IAgreementListItem {
    return {
        Id: source.Id,
        Type: source.Type,
        Status: source.Status,
        Name: source.Name,
        Description: source.Description,
        AgreementNumber: source.Description,
        ValidFrom: source.ValidFrom,
        ValidTo: source.ValidTo,
    }
}

export function parseFromAgreement(source: IAgreementListItem): IRelatedAgreementListItem{
    return {
        Id: source.Id,
        Type: source.Type,
        Status: source.Status,
        Name: source.Name,
        Description: source.Description,
        AgreementNumber: source.AgreementNumber,
        ValidFrom: null,
        ValidTo: null,
    }
}

export interface IServiceListItem {
    Id: number;
    Status: string;
    Name: string;
    Icon: string,
    Validity: string;
    ServiceType: string;
    OwnId: string;
    CommercialDescription: string;

    OwnerId: number | null;
    OwnerName: string;

    ServiceProfileId: number | null;
    ServiceProfileName: string;

    ServiceProfileGroupId: number | null;
    ServiceProfileGroupName: string;
}

export interface IDeviceListItem {
    HdKind: KindsList,

    Id: number;
    Status: string;
    Name: string;
    
    InventoryNumber: string;
    Model: string;
    SerialNumber: string;
    Type: string;

    Description: string;
}

export interface IRelatedDeviceListItem {
    HdKind: KindsList,

    AssetId: number;
    AssetKind: AssetKind,
    Status: string;
    Name: string;
    
    Model: string;
    SerialNumber: string;
    InventoryNumber: string;
    Description: string;
}

export function parseToDevice(source: IRelatedDeviceListItem): IDeviceListItem {
    return {
        Id: source.AssetId,
        Type: null,
        HdKind: source.HdKind,
        Status: source.Status,
        Name: source.Name,
        InventoryNumber: source.InventoryNumber,
        Model: source.Model,
        SerialNumber: source.SerialNumber,
        Description: source.Description,
    }
}

export function parseFromDevice(source: IDeviceListItem): IRelatedDeviceListItem {
    return {
        AssetId: source.Id,
        HdKind: source.HdKind,
        Status: source.Status,
        Name: source.Name,
        InventoryNumber: source.InventoryNumber,
        Model: source.Model,
        SerialNumber: source.SerialNumber,
        Description: source.Description,
        AssetKind: AssetKind.Unknown,
    }
}

export enum AssetKind {
    Unknown = "Unknown",

    Computer = "Computer",
    Server = "Server",
    VirtualMachine = "VirtualMachine",
    License = "License",
    Person = "Person",
    Software = "Software",
    Attachment = "Attachment",
    Monitor = "Monitor",
    MobileDevice = "MobileDevice",
    Printer = "Printer",
    DataCarrier = "DataCarrier",
    Department = "Department",
    Location = "Location",
    NetworkDevice = "NetworkDevice",
    OtherNetworkDevice = "OtherNetworkDevice",

    PhoneNumber = "PhoneNumber",

    Agreement = "Agreement",
    ItSystem = "ItSystem",
    LogService = "LogService",
    Others = "Others",
    Contractor = "Contractor",
    LogServiceCustomer = "LogServiceCustomer",

    Service = "Service",
}